import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Save, Building2, MapPin, DollarSign, Key, Shield, Settings as SettingsIcon, Lock, FileText, Download, Database, Upload, Trash2, Building, Mail, Eye, EyeOff, Check, Loader, X, Globe, Phone, Users, Image, Briefcase, Clock } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { CompanyStamp } from '../components/CompanyStamp';

const Settings = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_address: '',
    company_postal_code: '',
    company_city: '',
    vat_id: '',
    tax_rate: 19.0,
    labor_rate: 85.0,
    invoice_rate_id: null,
    logo_url: null,
    // Additional company fields
    managing_director: '',
    phone: '',
    email: '',
    website: '',
    commercial_register: '',
    tax_number: '',
    employee_count: 0
  });

  const [datSettings, setDatSettings] = useState({
    api_key: '',
    api_url: '',
    customerNumber: '',
    userLogin: '',
    signatureKey: '',
    interfacePartnerNumber: '',
    interfacePartnerSignature: '',
    productVariant: 'damage.calc'  // SilverDAT Pro: Schadenkalkulation
  });

  const [gdvSettings, setGdvSettings] = useState({
    bearer_token: '',
    requestor: '',
    password: ''
  });

  const [accessStatus, setAccessStatus] = useState({
    dat_status: 'not_requested',
    gdv_status: 'not_requested'
  });
  
  const [requestingAccess, setRequestingAccess] = useState({
    dat: false,
    gdv: false
  });

  const [signature, setSignature] = useState(null);
  const [companyStamp, setCompanyStamp] = useState(null);
  const [abtretungsText, setAbtretungsText] = useState('');

  // Email Settings State
  const [emailProviders, setEmailProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'checking', 'success', 'error'
  const [connectionMessage, setConnectionMessage] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  
  const [emailConfig, setEmailConfig] = useState({
    email_address: '',
    password: '',
    provider: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_use_tls: true,
    imap_host: '',
    imap_port: 993,
    imap_use_ssl: true,
    display_name: '',
    signature: ''
  });

  useEffect(() => {
    fetchCompanySettings();
    fetchDatSettings();
    fetchEmailProviders();
    fetchEmailConfig();
    fetchAccessStatus();
  }, []);

  const fetchAccessStatus = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/integrations/access-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccessStatus({
          dat_status: data.dat_status || 'not_requested',
          gdv_status: data.gdv_status || 'not_requested'
        });
      }
    } catch (error) {
      console.error('Error fetching access status:', error);
    }
  };

  const requestApiAccess = async (apiType) => {
    setRequestingAccess(prev => ({ ...prev, [apiType.toLowerCase()]: true }));
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/integrations/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ api_type: apiType })
      });

      if (response.ok) {
        toast.success(`${apiType}-Zugang erfolgreich beantragt!`);
        await fetchAccessStatus(); // Reload status
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Beantragen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setRequestingAccess(prev => ({ ...prev, [apiType.toLowerCase()]: false }));
    }
  };

  const fetchDatSettings = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/settings/dat-myclaim`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_credentials) {
          setDatSettings({
            customerNumber: data.customer_number || '',
            userLogin: data.user_login || '',
            signatureKey: data.signature_key_masked || '',
            interfacePartnerNumber: data.interface_partner_number || '',
            interfacePartnerSignature: data.interface_partner_signature || '',
            productVariant: data.product_variant || 'damage.calc'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching DAT settings:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/settings/company', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data) {
          setCompanySettings(data);
          setSignature(data.signature_url || null);
          setCompanyStamp(data.stamp_url || null);
          setAbtretungsText(data.abtretung_text || '');
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveSignature = async (signatureData) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/settings/signature`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ signature: signatureData })
      });

      if (response.ok) {
        setSignature(signatureData);
        toast.success('✅ Unterschrift gespeichert!');
      } else {
        toast.error('Fehler beim Speichern der Unterschrift');
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleSaveStamp = async (stampData) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/settings/stamp`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ stamp: stampData })
      });

      if (response.ok) {
        setCompanyStamp(stampData);
        toast.success('✅ Firmenstempel gespeichert!');
      } else {
        toast.error('Fehler beim Speichern des Stempels');
      }
    } catch (error) {
      console.error('Error saving stamp:', error);
      toast.error('Verbindungsfehler');
    }
  };

  // Logo Upload Handler
  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte nur Bilddateien hochladen (JPG, PNG, etc.)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei ist zu groß. Maximal 5MB erlaubt.');
      return;
    }
    
    setLogoUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${getBackendUrl()}/api/company/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanySettings(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('✅ Logo erfolgreich hochgeladen!');
      } else {
        toast.error('❌ Fehler beim Hochladen des Logos');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('❌ Verbindungsfehler beim Hochladen');
    } finally {
      setLogoUploading(false);
    }
  };
  
  const handleLogoDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  }, []);
  
  const handleLogoDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  }, []);
  
  const handleLogoDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleLogoUpload(files[0]);
    }
  }, []);
  
  const handleLogoFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleLogoUpload(files[0]);
    }
  };
  
  const handleRemoveLogo = () => {
    setCompanySettings(prev => ({ ...prev, logo_url: '' }));
    toast.info('Logo entfernt. Speichern Sie die Änderungen.');
  };

  const handleExportData = async () => {
    try {
      toast.info('🔄 Datenexport wird vorbereitet...');
      
      const response = await fetch(`${getBackendUrl()}/api/export/all-data`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `easyclaim24_export_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('✅ Datenexport erfolgreich heruntergeladen!');
      } else {
        toast.error('❌ Fehler beim Exportieren der Daten');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('❌ Verbindungsfehler beim Exportieren');
    }
  };

  const handleSaveAbtretung = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/settings/abtretung`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ abtretung_text: abtretungsText })
      });

      if (response.ok) {
        toast.success('✅ Abtretungserklärung gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving abtretung:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(getBackendUrl() + '/api/settings/company', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(companySettings)
      });

      if (response.ok) {
        toast.success('Firmen-Einstellungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDAT = async (e) => {
    e.preventDefault();
    
    // Check if it's GDV credentials (old fields) or DAT myClaim credentials (new fields)
    const isGDVCredentials = datSettings.api_key && datSettings.api_url;
    const isDATCredentials = datSettings.customerNumber || datSettings.userLogin || datSettings.signatureKey;
    
    if (isDATCredentials) {
      // Save DAT myClaim Credentials
      if (!datSettings.customerNumber || !datSettings.userLogin || !datSettings.signatureKey) {
        toast.error('Bitte alle DAT-Felder ausfüllen');
        return;
      }
      
      setLoading(true);

      try {
        const response = await fetch(`${getBackendUrl()}/api/settings/dat-myclaim`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customerNumber: datSettings.customerNumber,
            userLogin: datSettings.userLogin,
            signatureKey: datSettings.signatureKey,
            // Partner-Daten werden automatisch aus Kundennummer/Passwort übernommen (Backend-Fallback)
            interfacePartnerNumber: '',
            interfacePartnerSignature: '',
            productVariant: datSettings.productVariant
          })
        });

        if (response.ok) {
          toast.success('DAT Credentials erfolgreich gespeichert!');
          
          // Reload settings to show masked password
          await fetchDatSettings();
          await fetchAccessStatus(); // Reload access status (should now be "live")
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Fehler beim Speichern');
        }
      } catch (error) {
        toast.error('Verbindungsfehler');
      } finally {
        setLoading(false);
      }
    } else if (isGDVCredentials) {
      // Save GDV Credentials (old logic for backward compatibility)
      setLoading(true);

      try {
        const response = await fetch(`${getBackendUrl()}/api/settings/api/dat?api_key=${encodeURIComponent(datSettings.api_key)}&api_url=${encodeURIComponent(datSettings.api_url)}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          toast.success('✅ GDV Zugangsdaten sicher gespeichert!');
          // Felder leeren nach Speichern
          setDatSettings({ ...datSettings, api_key: '', api_url: '' });
          await fetchAccessStatus(); // Reload access status (should now be "live")
        } else {
          toast.error('Fehler beim Speichern');
        }
      } catch (error) {
        toast.error('Verbindungsfehler');
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('Bitte Felder ausfüllen');
    }
  };

  const handleSaveGDV = async (e) => {
    e.preventDefault();
    
    if (!gdvSettings.bearer_token) {
      toast.error('Bitte Bearer Token eingeben');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${getBackendUrl()}/api/settings/api/gdv?bearer_token=${encodeURIComponent(gdvSettings.bearer_token)}&requestor=${encodeURIComponent(gdvSettings.requestor)}&password=${encodeURIComponent(gdvSettings.password)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('✅ GDV Credentials sicher gespeichert!');
        // Lösche Felder nach Speichern (aus Sicherheitsgründen)
        setGdvSettings({ bearer_token: '', requestor: '', password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  // Email Functions
  const fetchEmailProviders = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/providers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailProviders(data.providers);
      }
    } catch (error) {
      console.error('Error loading email providers:', error);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.configured) {
          setEmailConfig(prev => ({ ...prev, ...data, password: '' }));
          setSelectedProvider(data.provider);
        }
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const handleEmailProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    setShowProviderDropdown(false);
    if (emailProviders[providerId]) {
      const provider = emailProviders[providerId];
      setEmailConfig(prev => ({
        ...prev,
        provider: providerId,
        smtp_host: provider.smtp_host,
        smtp_port: provider.smtp_port,
        smtp_use_tls: provider.smtp_use_tls,
        imap_host: provider.imap_host,
        imap_port: provider.imap_port,
        imap_use_ssl: provider.imap_use_ssl
      }));
    }
  };

  const getProviderLogo = (providerId) => {
    switch(providerId) {
      case 'gmail':
        return (
          <svg viewBox="0 0 24 24" className="w-8 h-8">
            <path fill="#EA4335" d="M24 12.073c0-.773-.07-1.515-.2-2.232H12.24v4.224h6.596a5.636 5.636 0 01-2.444 3.696v2.985h3.954c2.316-2.13 3.654-5.268 3.654-8.673z"/>
            <path fill="#34A853" d="M12.24 24c3.302 0 6.076-1.096 8.1-2.969l-3.954-3.069c-1.095.734-2.496 1.168-4.146 1.168-3.187 0-5.886-2.154-6.848-5.048H1.332v3.165A11.998 11.998 0 0012.24 24z"/>
            <path fill="#FBBC05" d="M5.392 14.082a7.197 7.197 0 010-4.596V6.321H1.332a11.998 11.998 0 000 10.926l4.06-3.165z"/>
            <path fill="#4285F4" d="M12.24 4.773c1.797 0 3.41.618 4.68 1.831l3.51-3.51C18.31 1.192 15.542 0 12.24 0A11.998 11.998 0 001.332 6.321l4.06 3.165c.962-2.894 3.661-5.048 6.848-5.048z"/>
          </svg>
        );
      case 'outlook':
        return (
          <svg viewBox="0 0 24 24" className="w-8 h-8">
            <path fill="#0078D4" d="M24 12.26v7.35A2.4 2.4 0 0121.6 22H12V2h9.6a2.4 2.4 0 012.4 2.4v7.86z"/>
            <path fill="#0364B8" d="M11.45 2H2.4A2.4 2.4 0 000 4.4v15.2A2.4 2.4 0 002.4 22h9.05V2z"/>
            <path fill="#FFF" d="M11.45 13.38c0 2.23-1.68 4-4.2 4s-4.2-1.77-4.2-4 1.68-4 4.2-4 4.2 1.77 4.2 4z"/>
            <path fill="#0078D4" d="M7.25 10.73c-1.43 0-2.59 1.17-2.59 2.65s1.16 2.65 2.59 2.65 2.59-1.17 2.59-2.65-1.16-2.65-2.59-2.65z"/>
          </svg>
        );
      case 'yahoo':
        return (
          <svg viewBox="0 0 24 24" className="w-8 h-8">
            <path fill="#5F01D1" d="M22.9 5.9l-6.8 16.2h-3.1l3.5-8.3-5.6-7.9h3.3l3.7 5.4 3.7-5.4h3.3z"/>
            <circle fill="#FF0084" cx="4.4" cy="18.8" r="2.8"/>
            <path fill="#5F01D1" d="M11.4 5.9L7.9 13 4.4 5.9H1.1l5.3 11.3v5.1h3.1v-5.1l5.3-11.3z"/>
          </svg>
        );
      case 'gmx':
        return (
          <div className="w-8 h-8 flex items-center justify-center rounded bg-gradient-to-br from-blue-600 to-blue-800">
            <span className="text-white font-bold text-xs">GMX</span>
          </div>
        );
      case 'webde':
        return (
          <div className="w-8 h-8 flex items-center justify-center rounded bg-gradient-to-br from-yellow-500 to-yellow-600">
            <span className="text-white font-bold text-[10px]">WEB.DE</span>
          </div>
        );
      case 'ionos':
        return (
          <div className="w-8 h-8 flex items-center justify-center rounded bg-gradient-to-br from-blue-700 to-blue-900">
            <span className="text-white font-bold text-[10px]">IONOS</span>
          </div>
        );
      case 'strato':
        return (
          <div className="w-8 h-8 flex items-center justify-center rounded bg-gradient-to-br from-green-600 to-green-700">
            <span className="text-white font-bold text-[9px]">STRATO</span>
          </div>
        );
      default:
        return <Mail className="w-8 h-8 text-gray-400" />;
    }
  };

  const getProviderName = (providerId) => {
    const names = {
      'gmail': 'Gmail',
      'outlook': 'Outlook',
      'yahoo': 'Yahoo Mail',
      'gmx': 'GMX',
      'webde': 'Web.de',
      'ionos': 'IONOS',
      'strato': 'Strato'
    };
    return names[providerId] || 'Provider auswählen';
  };

  const testEmailConnection = async (autoCheck = false) => {
    if (!emailConfig.email_address || !emailConfig.password) {
      if (!autoCheck) {
        toast.error('Bitte Email-Adresse und Passwort eingeben');
      }
      return;
    }
    
    setTestingEmail(true);
    setConnectionStatus('checking');
    setConnectionMessage('Prüfe Verbindung...');
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailConfig)
      });
      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('success');
        setConnectionMessage('Verbindung erfolgreich! ✓');
        if (!autoCheck) {
          toast.success(data.message);
        }
      } else {
        setConnectionStatus('error');
        setConnectionMessage(`Fehler: ${data.message}`);
        if (!autoCheck) {
          toast.error(data.message);
        }
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionMessage('Verbindungsfehler');
      if (!autoCheck) {
        toast.error('Verbindungsfehler');
      }
    } finally {
      setTestingEmail(false);
    }
  };

  // Auto-check connection when email and password are filled
  const handleEmailConfigChange = (field, value) => {
    const newConfig = {...emailConfig, [field]: value};
    setEmailConfig(newConfig);
    
    // Reset connection status when user changes values
    setConnectionStatus(null);
    setConnectionMessage('');
    
    // Auto-check if both email and password are filled
    if (field === 'password' && newConfig.email_address && value) {
      // Delay check by 1 second to avoid checking on every keystroke
      setTimeout(() => {
        if (newConfig.email_address && newConfig.password) {
          testEmailConnection(true);
        }
      }, 1000);
    }
  };

  const saveEmailConfig = async () => {
    if (!emailConfig.email_address || !emailConfig.password) {
      toast.error('Bitte Email-Adresse und Passwort eingeben');
      return;
    }
    setSavingEmail(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailConfig)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">API & Einstellungen</h1>
              <p className="text-sm text-gray-600">Firmendaten und API-Integrationen konfigurieren</p>
            </div>
          </div>
        </div>

        {/* Grid Layout for Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Company Settings Card */}
          <div className="bg-white rounded-xl shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="p-5">
              {/* Section Header */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                <div className="w-12 h-12 flex items-center justify-center">
                  {companySettings.logo_url ? (
                    <img src={getBackendUrl() + companySettings.logo_url} alt="Firmenlogo" className="w-full h-full object-contain rounded" />
                  ) : (
                    <div className="w-full h-full bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Firmen-Einstellungen</h2>
                  <p className="text-xs text-gray-500">Basisdaten</p>
                </div>
              </div>
              <form onSubmit={handleSaveCompany} className="space-y-4">
                
                {/* Logo Upload Bereich mit Drag & Drop */}
                <div className="p-4 bg-gray-50 rounded-lg border" style={{ borderColor: '#E1E4E8' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Firmenlogo</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Drag & Drop Zone */}
                    <div
                      onDragOver={handleLogoDragOver}
                      onDragLeave={handleLogoDragLeave}
                      onDrop={handleLogoDrop}
                      onClick={() => document.getElementById('settings-logo-upload').click()}
                      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                        isDraggingLogo 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-300 hover:border-blue-400 hover:bg-white'
                      }`}
                      data-testid="settings-logo-dropzone"
                    >
                      <input
                        id="settings-logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileSelect}
                        className="hidden"
                      />
                      
                      {logoUploading ? (
                        <div className="flex flex-col items-center">
                          <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          <p className="text-sm text-gray-600">Wird hochgeladen...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload className={`w-8 h-8 mb-2 ${isDraggingLogo ? 'text-blue-500' : 'text-gray-400'}`} />
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            {isDraggingLogo ? 'Logo hier ablegen' : 'Logo hierher ziehen'}
                          </p>
                          <p className="text-xs text-gray-500">oder klicken zum Auswählen</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG bis 5MB</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Logo Preview */}
                    <div className="flex flex-col">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Vorschau</label>
                      <div className="flex-1 border rounded-xl p-4 bg-white flex items-center justify-center min-h-[120px]" style={{ borderColor: '#E1E4E8' }}>
                        {companySettings.logo_url ? (
                          <div className="relative group">
                            <img 
                              src={companySettings.logo_url.startsWith('/') ? getBackendUrl() + companySettings.logo_url : companySettings.logo_url}
                              alt="Firmenlogo" 
                              className="max-h-24 w-auto object-contain"
                              data-testid="settings-logo-preview"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveLogo();
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Logo entfernen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="text-center text-gray-400">
                            <Building className="w-10 h-10 mx-auto mb-1 opacity-50" />
                            <p className="text-xs">Kein Logo</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Firmendaten */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Firmenname *</label>
                    <input
                      type="text"
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      style={{ borderColor: '#E1E4E8' }}
                      placeholder="Easyclaim24 GmbH"
                      required
                      data-testid="company-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Firmenname *</label>
                    <input
                      type="text"
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      style={{ borderColor: '#E1E4E8' }}
                      placeholder="Easyclaim24 GmbH"
                      required
                      data-testid="company-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Adresse *</label>
                    <input
                      type="text"
                      value={companySettings.company_address}
                      onChange={(e) => setCompanySettings({...companySettings, company_address: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      style={{ borderColor: '#E1E4E8' }}
                      placeholder="Straße"
                      required
                      data-testid="company-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">PLZ *</label>
                      <input
                        type="text"
                        value={companySettings.company_postal_code}
                        onChange={(e) => setCompanySettings({...companySettings, company_postal_code: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        placeholder="10115"
                        required
                        data-testid="postal-code-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Stadt *</label>
                      <input
                        type="text"
                        value={companySettings.company_city}
                        onChange={(e) => setCompanySettings({...companySettings, company_city: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        placeholder="Berlin"
                        required
                        data-testid="city-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">USt-IdNr.</label>
                      <input
                        type="text"
                        value={companySettings.vat_id}
                        onChange={(e) => setCompanySettings({...companySettings, vat_id: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        placeholder="DE123456789"
                        data-testid="vat-id-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Steuersatz (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={companySettings.tax_rate}
                        onChange={(e) => setCompanySettings({...companySettings, tax_rate: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        placeholder="19.0"
                        data-testid="tax-rate-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Stundensatz (€) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={companySettings.labor_rate}
                        onChange={(e) => setCompanySettings({...companySettings, labor_rate: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        required
                        placeholder="85.00"
                        data-testid="labor-rate-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">DAT Rate-ID</label>
                      <input
                        type="number"
                        min="0"
                        value={companySettings.invoice_rate_id || ''}
                        onChange={(e) => setCompanySettings({...companySettings, invoice_rate_id: e.target.value ? parseInt(e.target.value) : null})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        style={{ borderColor: '#E1E4E8' }}
                        placeholder="1000303"
                        data-testid="invoice-rate-id-input"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all hover:shadow-lg disabled:opacity-50"
                  style={{backgroundColor: '#3B82F6'}}
                  data-testid="save-company-button"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Speichert...' : 'Speichern'}
                </button>
              </form>
            </div>
          </div>

          {/* GDV API Settings */}
          {user.role === 'admin' && (
            <div className="bg-white rounded-xl shadow-sm border" style={{borderColor: '#E1E4E8'}}>
              <div className="p-5">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img src="/gdv-logo.png" alt="GDV Logo" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-base font-semibold text-gray-900">GDV Zugangsdaten</h2>
                    <p className="text-xs text-gray-500">Verschlüsselt</p>
                  </div>
                  {/* Status Badge */}
                  <div>
                    {accessStatus.gdv_status === 'not_requested' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Nicht beantragt
                      </span>
                    )}
                    {accessStatus.gdv_status === 'requested' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Clock className="w-3 h-3" />
                        Beantragt
                      </span>
                    )}
                    {accessStatus.gdv_status === 'live' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="w-3 h-3" />
                        Live
                      </span>
                    )}
                  </div>
                </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">🔒 Sicher gespeichert</p>
                  <p>Tragen Sie hier Ihren GDV Requestor und Passwort ein. Die Daten werden verschlüsselt gespeichert und sind für normale Benutzer unsichtbar.</p>
                </div>
              </div>
            </div>
                
                {/* Request Access Button (if not requested yet) */}
                {accessStatus.gdv_status === 'not_requested' && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => requestApiAccess('GDV')}
                      disabled={requestingAccess.gdv}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      data-testid="request-gdv-access-btn"
                    >
                      {requestingAccess.gdv ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Wird beantragt...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          GDV-Zugang beantragen
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Sie erhalten eine Bestätigung per Email
                    </p>
                  </div>
                )}
                
                {/* Show form only if access is live or requested */}
                {(accessStatus.gdv_status === 'requested' || accessStatus.gdv_status === 'live') && (
                <form onSubmit={handleSaveDAT} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">GDV Requestor *</label>
                    <input
                      type="password"
                      value={datSettings.api_key}
                      onChange={(e) => setDatSettings({...datSettings, api_key: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      style={{ borderColor: '#E1E4E8' }}
                      placeholder="Benutzername"
                      data-testid="dat-api-key-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">GDV Passwort *</label>
                    <input
                      type="password"
                      value={datSettings.api_url}
                      onChange={(e) => setDatSettings({...datSettings, api_url: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      style={{ borderColor: '#E1E4E8' }}
                      placeholder="Passwort"
                      data-testid="dat-api-url-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all hover:shadow-lg disabled:opacity-50"
                    style={{backgroundColor: '#3B82F6'}}
                    data-testid="save-dat-button"
                  >
                    <Save className="w-4 h-4" />
                    {loading ? 'Speichert...' : 'Speichern'}
                  </button>
                </form>
                )}
              </div>
            </div>
          )}
        </div>

        {/* DAT SilverDAT myClaim API Settings - Full Width */}
        <div className="bg-white rounded-xl shadow-sm border mb-6" style={{borderColor: '#E1E4E8'}}>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
              <div className="w-12 h-12 flex items-center justify-center">
                <img src="/dat-logo.png" alt="DAT Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900">DAT SilverDAT myClaim API</h2>
                <p className="text-xs text-gray-500">Persönliche Credentials für {user.email}</p>
              </div>
              {/* Status Badge */}
              <div>
                {accessStatus.dat_status === 'not_requested' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    Nicht beantragt
                  </span>
                )}
                {accessStatus.dat_status === 'requested' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="w-3 h-3" />
                    Beantragt
                  </span>
                )}
                {accessStatus.dat_status === 'live' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <Check className="w-3 h-3" />
                    Live
                  </span>
                )}
              </div>
            </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex gap-2">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium">👤 Persönliche DAT Credentials</p>
                <p>Diese Credentials gelten nur für Sie ({user.email}). Jeder Benutzer kann seine eigenen DAT-Zugangsdaten hinterlegen. Alle Daten werden verschlüsselt gespeichert.</p>
              </div>
            </div>
          </div>
            
            {/* Request Access Button (if not requested yet) */}
            {accessStatus.dat_status === 'not_requested' && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => requestApiAccess('DAT')}
                  disabled={requestingAccess.dat}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  data-testid="request-dat-access-btn"
                >
                  {requestingAccess.dat ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Wird beantragt...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      DAT-Zugang beantragen
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Sie erhalten eine Bestätigung per Email
                </p>
              </div>
            )}
            
            {/* Show form only if access is live or requested */}
            {(accessStatus.dat_status === 'requested' || accessStatus.dat_status === 'live') && (
            <form onSubmit={handleSaveDAT}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">DAT-Kundennummer *</label>
                  <input
                    type="text"
                    value={datSettings.customerNumber || ''}
                    onChange={(e) => setDatSettings({...datSettings, customerNumber: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{ borderColor: '#E1E4E8' }}
                    placeholder="12345678"
                    data-testid="dat-customer-number-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Benutzer *</label>
                  <input
                    type="text"
                    value={datSettings.userLogin || ''}
                    onChange={(e) => setDatSettings({...datSettings, userLogin: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{ borderColor: '#E1E4E8' }}
                    placeholder="Benutzername"
                    data-testid="dat-user-login-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Passwort *</label>
                  <input
                    type="password"
                    value={datSettings.signatureKey || ''}
                    onChange={(e) => setDatSettings({...datSettings, signatureKey: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{ borderColor: '#E1E4E8' }}
                    placeholder="Passwort"
                    data-testid="dat-password-input"
                  />
                </div>
              </div>
              
              {/* Kalkulationsart - Glaskalkulation oder Schadenkalkulation */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">Kalkulationsart</label>
                <select
                  value={datSettings.productVariant || 'glassrep.agp'}
                  onChange={(e) => setDatSettings({...datSettings, productVariant: e.target.value})}
                  className="w-full md:w-1/2 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  style={{ borderColor: '#E1E4E8' }}
                  data-testid="dat-product-variant-select"
                >
                  <option value="glassrep.agp">Glaskalkulation (Glasschäden)</option>
                  <option value="damage.calc">Schadenkalkulation (Karosserie/Lack)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Wählen Sie die Standard-Kalkulationsart für DAT myClaim
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 text-white text-sm font-medium rounded-lg transition-all hover:shadow-lg disabled:opacity-50"
                style={{backgroundColor: '#3B82F6'}}
                data-testid="save-dat-button"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Speichert...' : 'DAT Credentials Speichern'}
              </button>
            </form>
            )}
          </div>
        </div>

        {/* Signature & Stamp Section */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Signature Component */}
            <SignaturePad 
              onSave={handleSaveSignature}
              initialSignature={signature}
              title="Unterschrift des Sachbearbeiters"
              subtitle="Ihre Unterschrift wird automatisch in allen generierten Aufträgen angezeigt"
            />

            {/* Stamp Component */}
            <CompanyStamp 
              onSave={handleSaveStamp}
              initialStamp={companyStamp}
              title="Firmenstempel"
              subtitle="Laden Sie Ihren Firmenstempel hoch oder erstellen Sie einen digitalen Stempel"
            />
          </div>
          
          {/* Abtretungserklärung Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Abtretungserklärung</h3>
                <p className="text-sm text-gray-600">Passen Sie den Text der Abtretungserklärung an</p>
              </div>
            </div>
            <div className="space-y-4">
              <textarea
                value={abtretungsText}
                onChange={(e) => setAbtretungsText(e.target.value)}
                rows={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="Geben Sie hier Ihren individuellen Text für die Abtretungserklärung ein..."
                data-testid="abtretung-textarea"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Dieser Text wird im Wizard unter "Abtretungserklärung" verwendet
                </p>
                <button
                  type="button"
                  onClick={handleSaveAbtretung}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  data-testid="save-abtretung-button"
                >
                  <Save className="w-4 h-4" />
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Export Section */}
        {user.role === 'admin' && (
          <div className="bg-white rounded-xl shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Database className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Datenexport</h2>
                  <p className="text-xs text-gray-500">Alle Daten herunterladen</p>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Laden Sie alle Ihre Daten als ZIP-Datei herunter. Die Datei enthält alle Akten, Kunden, Aufgaben und Termine im JSON-Format.
                </p>
                <button
                  onClick={handleExportData}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  data-testid="export-data-button"
                >
                  <Download className="w-5 h-5" />
                  Alle Daten als ZIP herunterladen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GDV API Status */}
        {user.role === 'admin' && (
          <div className="bg-white rounded-xl shadow-sm border" style={{borderColor: '#E1E4E8'}}>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">GDV API Status</h2>
                  <p className="text-xs text-gray-500">Demo-Modus</p>
                </div>
              </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 text-sm">ℹ️ Demo-Modus aktiv</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                <strong>Hinweis:</strong> Ohne GDV-Credentials werden Dummy-Daten angezeigt. Nach Eintragen der echten Credentials werden Live-Abfragen durchgeführt.
              </p>
            </div>
            </div>
          </div>
        )}

        {/* Email Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border mt-6" style={{borderColor: '#E1E4E8'}}>
          <div className="p-6 border-b bg-blue-50" style={{borderColor: '#E1E4E8'}}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Email-Konfiguration</h2>
                <p className="text-sm text-gray-600">Email-Account für Versand und Empfang konfigurieren</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Provider Selection - Custom Dropdown with Logos */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email-Provider auswählen *</label>
              
              {/* Selected Provider Display */}
              <button
                type="button"
                onClick={() => setShowProviderDropdown(!showProviderDropdown)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white hover:border-blue-400 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  {selectedProvider ? (
                    <>
                      {getProviderLogo(selectedProvider)}
                      <span className="font-medium text-gray-900">{getProviderName(selectedProvider)}</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-8 h-8 text-gray-400" />
                      <span className="text-gray-500">Bitte Provider auswählen</span>
                    </>
                  )}
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${showProviderDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showProviderDropdown && (
                <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-auto">
                  {[
                    { id: 'gmail', name: 'Gmail', subtitle: 'Google Mail' },
                    { id: 'outlook', name: 'Outlook', subtitle: 'Office 365' },
                    { id: 'yahoo', name: 'Yahoo Mail', subtitle: 'Yahoo' },
                    { id: 'gmx', name: 'GMX', subtitle: 'GMX Mail' },
                    { id: 'webde', name: 'Web.de', subtitle: 'Web.de Mail' },
                    { id: 'ionos', name: 'IONOS', subtitle: '1&1 Mail' },
                    { id: 'strato', name: 'Strato', subtitle: 'Strato Mail' }
                  ].map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => handleEmailProviderChange(provider.id)}
                      className={`w-full p-3 flex items-center gap-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                        selectedProvider === provider.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded shadow-sm">
                        {getProviderLogo(provider.id)}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-semibold text-gray-900 text-sm">{provider.name}</div>
                        <div className="text-xs text-gray-500">{provider.subtitle}</div>
                      </div>
                      {selectedProvider === provider.id && (
                        <Check className="w-5 h-5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email-Adresse *</label>
              <input
                type="email"
                value={emailConfig.email_address}
                onChange={(e) => handleEmailConfigChange('email_address', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ihre@email.de"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passwort / App-Passwort *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={emailConfig.password}
                  onChange={(e) => handleEmailConfigChange('password', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Für Gmail, Outlook, etc. verwenden Sie bitte ein App-Passwort
              </p>
            </div>

            {/* Connection Status */}
            {connectionStatus && (
              <div className={`p-4 rounded-lg border-2 flex items-center gap-3 ${
                connectionStatus === 'checking' ? 'bg-blue-50 border-blue-200' :
                connectionStatus === 'success' ? 'bg-green-50 border-green-200' :
                'bg-red-50 border-red-200'
              }`}>
                {connectionStatus === 'checking' && (
                  <Loader className="w-5 h-5 animate-spin text-blue-600" />
                )}
                {connectionStatus === 'success' && (
                  <Check className="w-5 h-5 text-green-600" />
                )}
                {connectionStatus === 'error' && (
                  <X className="w-5 h-5 text-red-600" />
                )}
                <span className={`text-sm font-medium ${
                  connectionStatus === 'checking' ? 'text-blue-800' :
                  connectionStatus === 'success' ? 'text-green-800' :
                  'text-red-800'
                }`}>
                  {connectionMessage}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Anzeigename</label>
                <input
                  type="text"
                  value={emailConfig.display_name}
                  onChange={(e) => setEmailConfig({...emailConfig, display_name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ihr Name"
                />
              </div>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email-Signatur</label>
                <textarea
                  value={emailConfig.signature}
                  onChange={(e) => setEmailConfig({...emailConfig, signature: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Mit freundlichen Grüßen"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={testEmailConnection}
                disabled={testingEmail}
                className="flex-1 px-6 py-2 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {testingEmail ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Teste...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Verbindung testen
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={saveEmailConfig}
                disabled={savingEmail}
                className="flex-1 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingEmail ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Settings;
